import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';

dayjs.extend(quarterOfYear);

const shortDateFormat = 'MMM D, YYYY';

export enum DatePeriod {
	TODAY = 'Today',
	LAST_7_DAYS = 'Last 7 days',
	LAST_4_WEEKS = 'Last 4 weeks',
	LAST_3_MONTHS = 'Last 3 months',
	LAST_6_MONTHS = 'Last 6 months',
	LAST_12_MONTHS = 'Last 12 months',
	CURRENT_MONTH = 'Current month',
	CURRENT_QUARTER = 'Current quarter',
	CURRENT_YEAR = 'Current year',
	ALL_TIME = 'All time'
}

export enum ComparePeriod {
	PREVIOUS_PERIOD = 'Previous period',
	PREVIOUS_MONTH = 'Previous month',
	PREVIOUS_QUARTER = 'Previous quarter',
	PREVIOUS_YEAR = 'Previous year',
	NONE = 'No comparison'
}

export const getPeriodDate = (period: DatePeriod): dayjs.Dayjs => {
	const now = dayjs();
	switch (period) {
		case DatePeriod.TODAY:
			return now;
		case DatePeriod.LAST_7_DAYS:
			return now.subtract(7, 'day');
		case DatePeriod.LAST_4_WEEKS:
			return now.subtract(4, 'week');
		case DatePeriod.LAST_3_MONTHS:
			return now.subtract(3, 'month');
		case DatePeriod.LAST_6_MONTHS:
			return now.subtract(6, 'month');
		case DatePeriod.LAST_12_MONTHS:
			return now.subtract(12, 'month');
		case DatePeriod.CURRENT_MONTH:
			return now.startOf('month');
		case DatePeriod.CURRENT_QUARTER: {
			return now.startOf('quarter');
		}
		case DatePeriod.CURRENT_YEAR: {
			return now.startOf('year');
		}
		case DatePeriod.ALL_TIME: {
			return dayjs('2022-01-01');
		}
	}
};

export const getComparedPeriod = (
	period: DatePeriod,
	compared: ComparePeriod
): [compareFrom: dayjs.Dayjs, compareTo: dayjs.Dayjs] => {
	const now = dayjs();

	switch (compared) {
		case ComparePeriod.NONE:
			return [getPeriodDate(period), now];
		case ComparePeriod.PREVIOUS_PERIOD: {
			switch (period) {
				case DatePeriod.TODAY:
					return [now.subtract(1, 'day'), now.subtract(1, 'day')];
				case DatePeriod.LAST_7_DAYS:
					return [now.subtract(2, 'weeks'), now.subtract(8, 'days')];
				case DatePeriod.LAST_4_WEEKS:
					return [now.subtract(8, 'weeks'), now.subtract(4, 'weeks').subtract(1, 'day')];
				case DatePeriod.LAST_3_MONTHS:
					return [now.subtract(6, 'months'), now.subtract(3, 'months').subtract(1, 'day')];
				case DatePeriod.LAST_6_MONTHS:
					return [now.subtract(12, 'month'), now.subtract(6, 'months').subtract(1, 'day')];
				case DatePeriod.LAST_12_MONTHS:
					return [now.subtract(24, 'month'), now.subtract(12, 'months').subtract(1, 'day')];
				case DatePeriod.CURRENT_MONTH:
					return [getPeriodDate(period).subtract(1, 'month'), getPeriodDate(period).subtract(1, 'day')];
				case DatePeriod.CURRENT_QUARTER:
					return [getPeriodDate(period).subtract(3, 'months'), getPeriodDate(period).subtract(1, 'day')];
				case DatePeriod.CURRENT_YEAR:
					return [getPeriodDate(period).subtract(1, 'year'), getPeriodDate(period).subtract(1, 'day')];
			}
			return [getPeriodDate(period), now];
		}
		case ComparePeriod.PREVIOUS_MONTH:
			return [now.subtract(1, 'month').startOf('month'), now.subtract(1, 'month').endOf('month')];
		case ComparePeriod.PREVIOUS_QUARTER:
			return [now.subtract(1, 'quarter').startOf('quarter'), now.subtract(1, 'quarter').endOf('quarter')];
		case ComparePeriod.PREVIOUS_YEAR:
			return [now.subtract(1, 'year').startOf('year'), now.subtract(1, 'year').endOf('year')];
	}
};

export const getShortDatePeriod = (period: DatePeriod) =>
	`${dayjs(getPeriodDate(period)).format(shortDateFormat)} - ${dayjs().format(shortDateFormat)}`;

export const getShortComparePeriod = (period: DatePeriod, compare: ComparePeriod) => {
	const [from, to] = getComparedPeriod(period, compare);
	return `${dayjs(from).format(shortDateFormat)} - ${dayjs(to).format(shortDateFormat)}`;
};
